import { SubjectHash } from "./hash";
import { InitialUploadData } from "./upload/types";

export interface SessionManager {
	delete: (id: string) => Promise<void>;
}

export type Returns<TData, TError extends Error = Error> =
	| {
			data: TData;
			error: null;
			isLoading: false;
	  }
	| {
			data: null;
			error: TError;
			isLoading: false;
	  }
	| {
			data: null;
			error: null;
			isLoading: true;
	  };

// This can be consumed by React
export interface Session<
	TItems,
	TConfig extends Record<string, any> = Record<string, any>,
> {
	subject: SubjectHash;
	items: TItems[];
	errors: Error[] | null;
	status: ProcessStatus;
	progress: number; // 0 - 1
	config: TConfig;
	updatedAt: number;
}

// TODO: Add Download data
export type InitialSessionData<T extends SessionType> =
	T extends SessionType.Upload ? InitialUploadData : never;

export interface FileManagerConfiguration {
	uploads: {
		concurrent: number;
		maxCount: number;
		maxSize: number;
		multiple: boolean;
		// supported types
	};
	downloads: {
		concurrent: number;
	};
}

const SessionEvents = ["change", "cancelled", "done", "error"] as const;
export type SessionEvent = (typeof SessionEvents)[number];

export type EventPayload<
	E extends SessionEvent,
	F extends FileUploadOrDownload,
	C extends FileManagerConfiguration = FileManagerConfiguration,
> = E extends "change" ? Session<F, C> : E extends "error" ? Error : never;

export enum ProcessingStatus {
	Preparing = "preparing",
	InProgress = "in-progress",
}

export enum TerminationStatus {
	Idle = "idle",
	Ready = "ready",
	Done = "done",
	Error = "error",
	Cancelled = "cancelled",
}

export type ProcessStatus = TerminationStatus | ProcessingStatus;

export type BaseFile = {
	id: string;
	type: SessionType;
	progress: number;
	error: string | null;
};

export type FileUpload = BaseFile & {
	type: SessionType.Upload;
	source: File | null;
	sessionId: string;
	thumbnailSrc: string | null | undefined;
	name: string;
	ext: string;
} & (
		| {
				status:
					| TerminationStatus.Idle
					| TerminationStatus.Cancelled
					| TerminationStatus.Error;
				unifiedUri: null;
		  }
		| {
				status:
					| ProcessingStatus
					| TerminationStatus.Done
					| TerminationStatus.Ready;
				unifiedUri: string;
		  }
	);

export type FileDownload = BaseFile & {
	type: SessionType.Download;
	name: string;
	ext: string;
	unifiedUri: string;
	source: string | null;
	status: ProcessStatus;
};

export type FileUploadOrDownload = FileUpload | FileDownload;
export enum SessionType {
	Upload = "upload",
	Download = "download",
}

export type Rollback = () => void;
